import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'
import { Building2, Globe, Mail, Phone } from 'lucide-react'

import { StateSurface, StateSurfaceSkeleton } from '@/components/app/StateSurface'
import { PublicPageControls } from '@/components/app/PublicPageControls'
import { PublicFooter } from '@/components/app/PublicFooter'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import {
  useAuthSession,
  isSuspendedSession,
  isSuspendedAgentSession,
} from '@/features/auth/session'
import type { CurrentBusinessContact } from '@/types/auth'

function SuspendedPageNav() {
  return (
    <header className="fixed inset-x-0 top-0 z-30 flex h-12 items-center justify-between px-4 sm:px-6">
      <img src="/Uploads/logo-mark-light.svg" alt="HD Parrainage" className="h-7 w-auto dark:hidden" />
      <img src="/Uploads/logo-mark-dark.svg" alt="HD Parrainage" className="hidden h-7 w-auto dark:block" />
      <PublicPageControls />
    </header>
  )
}

function businessInitials(name: string | null | undefined) {
  const value = name?.trim()
  if (!value) return 'HD'
  return value
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
}

function formatWebsite(url: string) {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '')
}

function ContactRow({
  icon,
  label,
  value,
  href,
  external = false,
}: {
  icon: React.ReactNode
  label: string
  value: string
  href?: string
  external?: boolean
}) {
  const content = (
    <>
      <span className="flex size-9 shrink-0 items-center justify-center rounded-[0.625rem] bg-muted text-muted-foreground">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs text-muted-foreground">{label}</span>
        <span className="block truncate text-sm font-medium text-foreground">{value}</span>
      </span>
    </>
  )

  if (href) {
    return (
      <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        className="flex items-center gap-3 rounded-[0.625rem] border border-border px-3 py-2.5 transition-colors hover:bg-muted"
      >
        {content}
      </a>
    )
  }

  return (
    <div className="flex items-center gap-3 rounded-[0.625rem] border border-border px-3 py-2.5">
      {content}
    </div>
  )
}

function BusinessContactDialog({
  contact,
  fallbackBusinessName,
  open,
  onOpenChange,
}: {
  contact: CurrentBusinessContact | null
  fallbackBusinessName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const businessName = contact?.business_name ?? fallbackBusinessName
  const hasContact = contact?.business_website_url || contact?.contact_email || contact?.contact_phone

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden rounded-[0.625rem] p-0 shadow-none sm:max-w-md">
        <DialogHeader className="px-6 pb-5 pt-6 text-left">
          <div className="flex items-start gap-3 pr-8">
            <Avatar className="size-11 rounded-[0.625rem] border border-border bg-background">
              <AvatarImage
                src={contact?.business_logo_url ?? undefined}
                alt={businessName}
                className="object-contain"
              />
              <AvatarFallback className="rounded-[0.625rem] text-xs font-semibold">
                {businessInitials(businessName)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <DialogTitle className="truncate text-base">{businessName}</DialogTitle>
              <DialogDescription className="mt-1">
                {contact?.contact_name ?? t('system.suspendedAgent.contactDialogDescription')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Separator className="mb-5" />

        <div className="flex flex-col gap-2.5 px-6 text-sm">
          {contact?.business_website_url ? (
            <ContactRow
              icon={<Globe aria-hidden="true" />}
              label={t('system.suspendedAccount.websiteLabel')}
              value={formatWebsite(contact.business_website_url)}
              href={contact.business_website_url}
              external
            />
          ) : null}

          {contact?.contact_email ? (
            <ContactRow
              icon={<Mail aria-hidden="true" />}
              label={t('common.email')}
              value={contact.contact_email}
              href={`mailto:${contact.contact_email}`}
            />
          ) : null}

          {contact?.contact_phone ? (
            <ContactRow
              icon={<Phone aria-hidden="true" />}
              label={t('common.phone')}
              value={contact.contact_phone}
              href={`tel:${contact.contact_phone}`}
            />
          ) : null}

          {!hasContact ? (
            <div className="flex flex-col items-center gap-2 rounded-[0.625rem] border border-border px-4 py-4 text-center text-sm text-muted-foreground">
              <Building2 className="size-5" aria-hidden="true" />
              {t('system.suspendedAccount.contactUnavailable')}
            </div>
          ) : null}
        </div>

        <DialogFooter className="px-6 pb-6 pt-5">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function SuspendedAccountPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { status, user, logout, logoutPending } = useAuthSession()
  const [contactDialogOpen, setContactDialogOpen] = useState(false)

  if (status === 'loading') {
    return <StateSurfaceSkeleton variant="fullscreen" size="lg" />
  }

  if (status !== 'authenticated' || user === null) {
    return <Navigate to="/login" replace />
  }

  if (!isSuspendedSession(user)) {
    return <Navigate to="/dashboard" replace />
  }

  const isAgent = isSuspendedAgentSession(user)
  const contact = user.current_business_contact
  const businessName = contact?.business_name ?? user.primary_business?.display_name ?? t('app.name')

  const reason =
    (isAgent
      ? user.agent_profile?.suspension_reason?.trim()
      : user.suspension_reason?.trim()) ||
    t('system.suspendedAccount.reasonFallback')

  const title = isAgent
    ? t('system.suspendedAccount.agentTitle')
    : t('system.suspendedAccount.adminTitle')

  const description = isAgent
    ? t('system.suspendedAccount.agentDescription', { business: businessName })
    : t('system.suspendedAccount.adminDescription')

  const handleSignOut = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <>
      <div className="relative flex h-dvh flex-col overflow-hidden">
        <SuspendedPageNav />
        <StateSurface
          variant="fullscreen"
          size="lg"
          tone="warning"
          className="flex-1 min-h-0 pt-16"
          asset={
            <img
              src="/Uploads/suspended.webp"
              alt={title}
              className="h-56 w-56 object-contain md:h-64 md:w-64"
            />
          }
          title={title}
          description={
            <div className="mx-auto flex max-w-xl flex-col items-center gap-4">
              <p>{description}</p>
              <div className="w-full rounded-[0.625rem] border border-border bg-card px-4 py-3 text-left">
                <p className="text-xs font-medium text-muted-foreground">
                  {t('system.suspendedAccount.reasonLabel')}
                </p>
                <p className="mt-1 text-sm leading-6 text-foreground">{reason}</p>
              </div>
            </div>
          }
          primaryAction={
            isAgent
              ? {
                  label: t('system.suspendedAccount.contactAction'),
                  onClick: () => setContactDialogOpen(true),
                }
              : undefined
          }
          secondaryAction={{
            label: logoutPending ? t('auth.signingOut') : t('auth.signOut'),
            onClick: () => {
              void handleSignOut()
            },
            variant: 'outline',
          }}
        />
        <PublicFooter />
      </div>

      {isAgent && (
        <BusinessContactDialog
          contact={contact}
          fallbackBusinessName={businessName}
          open={contactDialogOpen}
          onOpenChange={setContactDialogOpen}
        />
      )}
    </>
  )
}
